// deployment_class: customer_env_allowed
/**
 * The package — every UI element the template can hold, in one static tray.
 *
 * ── Modelled on macOS "Customize Toolbar…" (E3 2026-08-29) ─────────────────
 * E3, on the Finder sheet: *"That's how I want our UI editor to work."* And
 * earlier: *"the package that we have that we can drop and drag always has all
 * of the elements, whether they're there or not… we can't have doubles. But if
 * we drag one out of the UI, now we have a way to restore that… it's always in
 * the same default package."*
 *
 * Three details from the real sheet, each load-bearing:
 *
 * 1. THE GRID IS COMPLETE AND STATIC. Every item is always present, in the same
 *    position, whether or not it is in use. That is what makes the set
 *    discoverable by looking rather than by remembering, and what lets a
 *    position become muscle memory. In-use items DIM; they never leave.
 *
 * 2. ONLY WHAT CAN EARN ITS PLACE. Finder puts "Space" and "Flexible Space" in
 *    its grid; we tried that and removed them (see below). Copying a vocabulary
 *    is not the same as copying a model — the model is the complete, static
 *    palette; the vocabulary has to suit a page rather than a toolbar.
 *
 * 3. THE DEFAULT SET IS A DRAGGABLE ROW, NOT A BUTTON. Finder shows the default
 *    arrangement as a real toolbar beneath the grid: *"…or drag the default set
 *    into the toolbar."* You see exactly what you are getting before you commit,
 *    and it uses the same grammar as everything else rather than being a special
 *    command hiding behind a label.
 *
 * ── Where ours must differ, deliberately ───────────────────────────────────
 * Apple's sheet is drag-only. WCAG 2.2 AA is a locked floor here, so every tile
 * is also a button doing the same thing without a pointer. Building that now
 * costs a line each; retrofitting it later costs a rewrite.
 */
import { DEFAULT_ELEMENTS, isRepeatable, type ElementKind } from './template-state';
import { DraggableTile } from './TemplateDnd';

interface PackageEntry {
  kind: ElementKind;
  label: string;
  /** A glyph, so a tile is recognised by shape before it is read. */
  glyph: string;
}

/**
 * The whole vocabulary, in a fixed order that never changes.
 *
 * Content first, then layout — the same grouping Finder uses, where the spacers
 * sit among the items rather than in a separate section, but the ordering keeps
 * like with like.
 */
const PACKAGE_ELEMENTS: readonly PackageEntry[] = [
  { kind: 'eyebrow', label: 'Eyebrow', glyph: '▁' },
  { kind: 'tabs', label: 'Tab strip', glyph: '▤' },
  { kind: 'summary', label: 'Summary', glyph: '¶' },
];

// ── SPACERS REMOVED 2026-08-29 ─────────────────────────────────────────────
// E3: "Space, flexible space, eyebrow, that… none of that is functional. What
// is all that for? That's just wasted stuff."
//
// He is right, and the reason is structural rather than taste. Finder needs
// Space and Flexible Space because a toolbar is ONE ROW: the only way to
// position an item is to put emptiness beside it. A dashboard is a PAGE — a
// grid positions things, so a draggable gap has nothing to do. I copied them
// because Apple has them, not because they could ever earn their place here.
//
// TITLE is gone from the palette too: it is permanent (a module with no name
// cannot be identified), so offering it as something to place implied it could
// be taken away.
//
// What belongs here instead is DATA, not chrome — a KPI tile, a list, a chart,
// a table — each one a named ask against CSA, so placing a widget means "show
// me this data, here" and a widget CSA cannot yet serve is a visible gap in the
// work queue. That is the next pass; this removes what was provably useless
// rather than leaving it there looking like a feature.

function Tile({
  entry,
  inUse,
  onAdd,
}: {
  entry: PackageEntry;
  inUse: boolean;
  onAdd: () => void;
}): JSX.Element {
  // A spacer is never "in use" — you may place as many as the layout needs.
  const unavailable = inUse && !isRepeatable(entry.kind);

  // The tile is genuinely draggable now. It was not before: `DraggableTile`
  // existed and nothing imported it, so the palette rendered a plain button and
  // dragging did nothing at all. The export-reachability gate found that by
  // itself — an unreached export is not a shipped feature.
  return (
    <div className={`kd-pkg-tile ${unavailable ? 'inuse' : ''}`}>
      <DraggableTile kind={entry.kind} disabled={unavailable} onClick={onAdd} label={entry.label}>
        <span className="kd-pkg-glyph" aria-hidden="true">
          {entry.glyph}
        </span>
        <span className="kd-pkg-name">{entry.label}</span>
      </DraggableTile>
    </div>
  );
}

/** The default arrangement as one object — Finder's second row. */
function DefaultSet({ onApply }: { onApply: () => void }): JSX.Element {
  const byKind = new Map(PACKAGE_ELEMENTS.map((e) => [e.kind, e]));

  return (
    <div className="kd-pkg-default">
      <button type="button" onClick={onApply} aria-label="Apply the default set to this module">
        {DEFAULT_ELEMENTS.map((k) => {
          const e = byKind.get(k);
          return (
            <span className="kd-pkg-defitem" key={k}>
              <span aria-hidden="true">{e?.glyph}</span>
              <span>{e?.label}</span>
            </span>
          );
        })}
      </button>
    </div>
  );
}

export function TemplatePackage({
  isPlaced,
  onAdd,
  onApplyDefaults,
}: {
  isPlaced: (kind: ElementKind) => boolean;
  onAdd: (kind: ElementKind) => void;
  onApplyDefaults: () => void;
}): JSX.Element {
  return (
    <div className="kd-pkg" aria-label="Component package">
      <p className="kd-pkg-lead">Add elements to this module…</p>

      <div className="kd-pkg-grid">
        {PACKAGE_ELEMENTS.map((e) => (
          <Tile key={e.kind} entry={e} inUse={isPlaced(e.kind)} onAdd={() => onAdd(e.kind)} />
        ))}
      </div>

      <p className="kd-pkg-lead">…or apply the default set.</p>
      <DefaultSet onApply={onApplyDefaults} />
    </div>
  );
}
